import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, Eye, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { reloadToHome, requestPlatformAdminAction } from '@/lib/platformAdmin';
import { logger } from '@/lib/logger';

const REFUSAL_TEXT: Record<string, string> = {
    'not-platform-admin': 'This account is not a platform admin.',
    'account-disabled': 'This account is disabled.',
    'profile-inactive': 'This account is deactivated.',
    'org-not-found': 'That organization no longer exists.',
    'purpose-required': 'State why you are opening this organization (at least 10 characters).',
};

// Mirrors PURPOSE_MIN / PURPOSE_MAX in functions/src/platformAdmin/platformAdminHandlers.ts.
const PURPOSE_MIN = 10;
const PURPOSE_MAX = 500;

/**
 * PLATFORM_ADMIN org switcher and read-only indicator, a chip in the header row
 * (WEB-100). Renders nothing for
 * anyone without the platformAdmin claim. Opening an org files a request the
 * server logs before it grants the view (src/lib/platformAdmin.ts).
 */
export const PlatformAdminBar: React.FC = () => {
    const { platformAdmin } = useAuth();
    const { organization } = useOrganization();
    const [orgs, setOrgs] = useState<{ id: string; name: string }[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [purpose, setPurpose] = useState('');
    // WEB-100: the switcher lives in a popover under the header chip, not a full-width row.
    const [open, setOpen] = useState(false);
    const purposeOk = purpose.trim().length >= PURPOSE_MIN && purpose.length <= PURPOSE_MAX;

    const run = useCallback(async (action: 'list' | 'start' | 'end', orgId: string | null = null, why?: string) => {
        setBusy(true);
        setError(null);
        try {
            const outcome = await requestPlatformAdminAction(action, orgId, why);
            if (outcome.status === 'refused') {
                setError(REFUSAL_TEXT[outcome.reason] ?? `Refused (${outcome.reason}).`);
                return;
            }
            if (action === 'list') {
                setOrgs(outcome.orgs ?? []);
                return;
            }
            reloadToHome();
        } catch (err) {
            logger.error('Platform admin request failed:', err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    }, []);

    // WEB-100: Escape closes the popover (the repo's dialog convention).
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // Optional chaining: layout tests mock useAuth without the platform-admin fields.
    if (!platformAdmin?.isPlatformAdmin) return null;

    if (!platformAdmin.qualifyingSession) {
        // WEB-100: a short chip; the full sentence rides in the title so it costs no row.
        return (
            <div
                role="region"
                aria-label="Platform admin"
                title="Platform admin needs a Google sign-in with your @aams.ai account. Sign out, then choose Continue with Google."
                className="flex items-center gap-2 rounded-xl border border-divider-subtle bg-bg-secondary/60 px-3 py-2 text-sm text-text-secondary"
            >
                <ShieldCheck size={16} aria-hidden="true" />
                <span className="hidden md:inline">Google sign-in needed</span>
            </div>
        );
    }

    const viewing = platformAdmin.viewOrgId;
    const endsAt = platformAdmin.viewExpiresAt
        ? new Date(platformAdmin.viewExpiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : null;

    const toggle = () => {
        if (open) {
            setOpen(false);
            return;
        }
        setOpen(true);
        if (orgs === null) void run('list');
    };

    // WEB-100: the purpose field and the organization list, under the chip.
    // Shared by the idle chip ("View an organization") and the viewing chip
    // ("Switch organization"), as the old bar offered both.
    const popover = open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-[min(28rem,calc(100vw-2rem))] rounded-xl border border-divider-subtle bg-bg-secondary p-3 text-sm shadow-lg">
            <div className="flex flex-col gap-2">
                {orgs !== null && (
                    <>
                        <label className="flex items-center gap-2">
                            <span className="sr-only">Purpose of this view</span>
                            <input
                                type="text"
                                value={purpose}
                                maxLength={PURPOSE_MAX}
                                onChange={(e) => setPurpose(e.target.value)}
                                placeholder="Purpose (support ticket, issue, security check)"
                                aria-describedby={purposeOk ? undefined : 'platform-admin-purpose-hint'}
                                className="w-full rounded-lg border border-divider-subtle bg-bg-primary px-2 py-1.5"
                            />
                        </label>
                        <label className="flex items-center gap-2">
                            <span className="sr-only">Organization to view</span>
                            <select
                                disabled={busy || !purposeOk}
                                value=""
                                onChange={(e) => e.target.value && run('start', e.target.value, purpose)}
                                aria-describedby={purposeOk ? undefined : 'platform-admin-purpose-hint'}
                                className="w-full rounded-lg border border-divider-subtle bg-bg-primary px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:border-dashed"
                            >
                                <option value="">Choose an organization</option>
                                {orgs.map((o) => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                            </select>
                        </label>
                        {!purposeOk && (
                            <p id="platform-admin-purpose-hint" className="text-xs text-text-secondary">
                                Enter a purpose of at least {PURPOSE_MIN} characters to choose an organization ({purpose.trim().length}/{PURPOSE_MIN}).
                            </p>
                        )}
                    </>
                )}
                {busy && <p className="text-text-secondary" aria-live="polite">Working...</p>}
                {error && <p className="text-red-400" role="alert">{error}</p>}
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="self-end rounded-lg border border-divider-subtle px-2.5 py-1 text-xs hover:bg-bg-primary"
                >
                    Close
                </button>
            </div>
        </div>
    );

    if (viewing) {
        // WEB-100: the audit indicator stays amber and prominent, now as a chip in the header row.
        return (
            <div
                role="region"
                aria-label="Platform admin"
                className="relative flex items-center gap-2 rounded-xl border border-amber-500/60 bg-amber-500/15 px-3 py-1.5 text-sm"
            >
                <p className="flex items-center gap-2 font-medium" role="status">
                    <Eye size={16} aria-hidden="true" />
                    <span>
                        Viewing <strong>{organization?.name ?? viewing}</strong> as platform admin. Read only.
                        {endsAt && <> Ends at {endsAt}.</>}
                    </span>
                </p>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => run('end')}
                    aria-label="Back to my organization"
                    className="flex items-center gap-1.5 rounded-lg border border-divider-subtle px-2.5 py-1 hover:bg-bg-secondary disabled:opacity-50"
                >
                    <LogOut size={14} aria-hidden="true" />
                    <span className="hidden md:inline">Back to my organization</span>
                </button>
                <button
                    type="button"
                    disabled={busy && orgs === null}
                    onClick={toggle}
                    aria-label="Switch organization"
                    aria-expanded={open}
                    className="flex items-center gap-1.5 rounded-lg border border-divider-subtle px-2.5 py-1 hover:bg-bg-secondary disabled:opacity-50"
                >
                    <ArrowLeftRight size={14} aria-hidden="true" />
                    <span className="hidden md:inline">Switch organization</span>
                </button>
                {busy && !open && <span className="text-text-secondary" aria-live="polite">Working...</span>}
                {error && !open && <span className="text-red-400" role="alert">{error}</span>}
                {popover}
            </div>
        );
    }

    return (
        <div role="region" aria-label="Platform admin" className="relative">
            <button
                type="button"
                disabled={busy && orgs === null}
                onClick={toggle}
                aria-label="View an organization"
                aria-expanded={open}
                className="flex items-center gap-2 rounded-xl border border-divider-subtle bg-bg-secondary/60 px-3 py-2 text-sm text-text-secondary hover:bg-bg-secondary disabled:opacity-50"
            >
                <ShieldCheck size={16} aria-hidden="true" />
                <span className="hidden md:inline">Platform admin</span>
            </button>

            {popover}
        </div>
    );
};
